import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowRight, CheckCircle2, LoaderCircle, MailCheck, ShieldCheck } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { verifyJobEmail } from "@/lib/job-verification.functions";

type Language = "de" | "fr" | "it" | "en";

const copy = {
  de: {
    loading: "E-Mail-Adresse wird sicher bestätigt…",
    confirmEyebrow: "E-Mail-Verifizierung",
    confirmTitle: "Bitte bestätigen Sie Ihre E-Mail-Adresse.",
    confirmText:
      "Mit Ihrer Bestätigung darf Begner den eingereichten Auftrag prüfen und bei Freigabe veröffentlichen.",
    confirmButton: "E-Mail jetzt bestätigen",
    successEyebrow: "E-Mail bestätigt",
    successTitle: "Vielen Dank. Ihr Auftrag kann jetzt geprüft werden.",
    successText:
      "Unser Team prüft die Angaben und Bilder. Sobald das Inserat freigegeben wurde, erhalten Sie eine weitere E-Mail.",
    reference: "Referenz",
    home: "Zur Startseite",
    post: "Weitere Anfrage eingeben",
    invalidEyebrow: "Link nicht gültig",
    invalidTitle: "Die E-Mail konnte nicht bestätigt werden.",
    invalidText:
      "Der Link ist möglicherweise abgelaufen oder wurde bereits verwendet. Bitte antworten Sie auf Ihre Begner-E-Mail, wenn Sie Hilfe benötigen.",
  },
  fr: {
    loading: "Confirmation sécurisée de l’adresse e-mail…",
    confirmEyebrow: "Vérification de l’e-mail",
    confirmTitle: "Veuillez confirmer votre adresse e-mail.",
    confirmText:
      "En confirmant, vous autorisez Begner à vérifier le mandat soumis et à le publier après validation.",
    confirmButton: "Confirmer l’e-mail",
    successEyebrow: "E-mail confirmé",
    successTitle: "Merci. Votre mandat peut maintenant être vérifié.",
    successText:
      "Notre équipe vérifie les informations et les photos. Vous recevrez un autre e-mail dès que l’annonce sera validée.",
    reference: "Référence",
    home: "Retour à l’accueil",
    post: "Publier un autre mandat",
    invalidEyebrow: "Lien non valable",
    invalidTitle: "L’adresse e-mail n’a pas pu être confirmée.",
    invalidText:
      "Le lien a peut-être expiré ou a déjà été utilisé. Répondez à votre e-mail Begner si vous avez besoin d’aide.",
  },
  it: {
    loading: "Conferma sicura dell’indirizzo e-mail…",
    confirmEyebrow: "Verifica e-mail",
    confirmTitle: "Confermi il suo indirizzo e-mail.",
    confirmText:
      "Con la conferma autorizza Begner a verificare l’incarico e a pubblicarlo dopo l’approvazione.",
    confirmButton: "Conferma l’e-mail",
    successEyebrow: "E-mail confermata",
    successTitle: "Grazie. Ora possiamo verificare il suo incarico.",
    successText:
      "Il nostro team verifica i dati e le immagini. Riceverà un’altra e-mail non appena l’annuncio sarà approvato.",
    reference: "Riferimento",
    home: "Alla pagina iniziale",
    post: "Pubblica un altro incarico",
    invalidEyebrow: "Link non valido",
    invalidTitle: "Non è stato possibile confermare l’indirizzo e-mail.",
    invalidText:
      "Il link potrebbe essere scaduto o già utilizzato. Risponda all’e-mail di Begner se ha bisogno di aiuto.",
  },
  en: {
    loading: "Securely confirming your email address…",
    confirmEyebrow: "Email verification",
    confirmTitle: "Please confirm your email address.",
    confirmText: "By confirming, you allow Begner to review and publish the submitted job.",
    confirmButton: "Confirm email now",
    successEyebrow: "Email confirmed",
    successTitle: "Thank you. Your job can now be reviewed.",
    successText:
      "Our team will review the details and images. You will receive another email once the listing has been approved.",
    reference: "Reference",
    home: "Back to home",
    post: "Post another job",
    invalidEyebrow: "Invalid link",
    invalidTitle: "The email address could not be confirmed.",
    invalidText:
      "The link may have expired or already been used. Please reply to your Begner email if you need help.",
  },
} as const;

export const Route = createFileRoute("/auftrag-bestaetigen")({
  staticData: { sitemap: false },
  validateSearch: (search: Record<string, unknown>): { token?: string; lang: Language } => ({
    token: typeof search.token === "string" ? search.token.slice(0, 128) : undefined,
    lang: search.lang === "fr" || search.lang === "it" || search.lang === "en" ? search.lang : "de",
  }),
  head: () => ({
    meta: [
      { title: "E-Mail bestätigen. Begner" },
      { name: "robots", content: "noindex, nofollow, noarchive" },
      { name: "referrer", content: "no-referrer" },
    ],
  }),
  component: VerifyJobEmailPage,
});

type State =
  | { status: "ready" }
  | { status: "loading" }
  | { status: "success"; reference: string }
  | { status: "invalid" };

function VerifyJobEmailPage() {
  const search = Route.useSearch();
  const text = copy[search.lang];
  const verify = useServerFn(verifyJobEmail);
  const validToken = Boolean(search.token && /^[a-f0-9]{64}$/.test(search.token));
  const [state, setState] = useState<State>(
    validToken ? { status: "ready" } : { status: "invalid" },
  );

  const confirm = () => {
    if (!search.token || state.status !== "ready") return;
    setState({ status: "loading" });
    void verify({ data: { token: search.token } })
      .then((result) =>
        setState(
          result.verified
            ? { status: "success", reference: result.reference }
            : { status: "invalid" },
        ),
      )
      .catch(() => setState({ status: "invalid" }));
  };

  if (state.status === "loading") {
    return (
      <main className="grid min-h-[65vh] place-items-center bg-muted/25 px-6 py-20">
        <div className="flex flex-col items-center text-center" role="status" aria-live="polite">
          <span className="grid size-16 place-items-center rounded-2xl bg-brand-soft text-primary shadow-card">
            <LoaderCircle className="size-7 animate-spin" />
          </span>
          <p className="mt-5 font-semibold text-muted-foreground">{text.loading}</p>
        </div>
      </main>
    );
  }

  const success = state.status === "success";
  const ready = state.status === "ready";
  return (
    <main className="relative grid min-h-[65vh] place-items-center overflow-hidden bg-muted/25 px-6 py-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-brand-soft/70 blur-3xl" />
      <Card className="relative w-full max-w-2xl overflow-hidden border-border/70 bg-card/95 shadow-card">
        <div className={`h-1.5 ${success ? "bg-gradient-brand" : "bg-muted-foreground/30"}`} />
        <CardContent className="flex flex-col items-center px-6 py-12 text-center sm:px-12">
          <span
            className={`grid size-16 place-items-center rounded-2xl ${
              success
                ? "bg-emerald-500/10 text-emerald-700"
                : ready
                  ? "bg-brand-soft text-primary"
                  : "bg-muted text-muted-foreground"
            }`}
          >
            {success ? <CheckCircle2 className="size-7" /> : <MailCheck className="size-7" />}
          </span>
          <p className="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-primary">
            {success ? text.successEyebrow : ready ? text.confirmEyebrow : text.invalidEyebrow}
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            {success ? text.successTitle : ready ? text.confirmTitle : text.invalidTitle}
          </h1>
          <p className="mt-4 max-w-xl leading-relaxed text-muted-foreground">
            {success ? text.successText : ready ? text.confirmText : text.invalidText}
          </p>
          {success ? (
            <div className="mt-6 inline-flex items-center gap-2 rounded-xl border border-primary/15 bg-brand-soft px-4 py-3 text-sm font-bold text-brand-deep">
              <ShieldCheck className="size-4" />
              {text.reference}: {state.reference}
            </div>
          ) : null}
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            {ready ? (
              <Button size="lg" className="rounded-xl shadow-brand" onClick={confirm}>
                <MailCheck className="size-4" />
                {text.confirmButton}
              </Button>
            ) : (
              <Button asChild size="lg" className="rounded-xl shadow-brand">
                <Link to="/">
                  {text.home}
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            )}
            {success ? (
              <Button asChild size="lg" variant="outline" className="rounded-xl">
                <Link to="/auftrag-einstellen">{text.post}</Link>
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
